import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

export default class extends Controller {
  static targets = ["status", "progress", "progressBar", "message", "actions", "planContent", "noplanContent", "errorAlert"]
  static values = { projectId: String }
  
  connect() {
    console.log("[ImplementationPlan] Controller connected for project:", this.projectIdValue)
    this.setupChannel()
  }
  
  disconnect() {
    if (this.channel) {
      console.log("[ImplementationPlan] Unsubscribing from channel")
      this.channel.unsubscribe()
    }
  }
  
  setupChannel() {
    this.channel = consumer.subscriptions.create(
      {
        channel: "AppProjectChannel",
        project_id: this.projectIdValue
      },
      {
        connected: () => {
          console.log("[ImplementationPlan] Connected to AppProjectChannel")
        },
        
        disconnected: () => {
          console.log("[ImplementationPlan] Disconnected from AppProjectChannel")
        },
        
        received: (data) => {
          console.log("[ImplementationPlan] Received:", data)
          this.handleMessage(data)
        }
      }
    )
  }
  
  handleMessage(data) {
    // Only handle implementation plan related messages
    switch(data.type) {
      case 'implementation_plan_status':
        this.updateStatus(data.status, data.message)
        break
      case 'implementation_plan_progress':
        this.updateProgress(data.percentage, data.message)
        break
      case 'implementation_plan_complete':
        this.handleComplete(data)
        break
      case 'implementation_plan_error':
        this.handleError(data.error)
        break
      // Ignore other message types like 'connected', 'status_update', etc
    }
  }
  
  updateStatus(status, message) {
    // Show progress section
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
    }
    
    // Hide no-plan content
    if (this.hasNoplanContentTarget) {
      this.noplanContentTarget.classList.add('hidden')
    }
    
    // Update status badge
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message
      
      // Update badge color based on status
      this.statusTarget.className = 'text-sm'
      if (status === 'analyzing') {
        this.statusTarget.classList.add('text-info')
      } else if (status === 'started') {
        this.statusTarget.classList.add('text-primary')
      }
    }
  }
  
  updateProgress(percentage, message) {
    // Update progress bar
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${percentage}%`
      this.progressBarTarget.textContent = `${percentage}%`
    }
    
    // Update message
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = message
    }
  }
  
  handleComplete(data) {
    // Update final status
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = data.message
      this.statusTarget.className = 'text-sm text-success font-semibold'
    }
    
    // Show success message and redirect option
    if (this.hasMessageTarget) {
      this.messageTarget.innerHTML = `
        <div class="alert alert-success">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="font-bold">Plan Generated Successfully!</h3>
            <div class="text-xs">Generated ${data.sections_count || 0} sections. Reloading page...</div>
          </div>
        </div>
      `
    }
    
    // Reload page after short delay to show new plan
    setTimeout(() => {
      window.location.reload()
    }, 2000)
  }
  
  handleError(error) {
    // Hide progress
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add('hidden')
    }
    
    // Show error
    if (this.hasErrorAlertTarget) {
      this.errorAlertTarget.classList.remove('hidden')
      this.errorAlertTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>${error}</span>
      `
    }
    
    // Re-enable generate button
    if (this.hasActionsTarget) {
      const button = this.actionsTarget.querySelector('input[type="submit"]')
      if (button) {
        button.disabled = false
        button.value = 'Retry Generation'
      }
    }
  }
  
  generatePlan(event) {
    // Prevent default form submission
    event.preventDefault()
    
    // Disable button
    const button = event.target.querySelector('input[type="submit"]')
    if (button) {
      button.disabled = true
      button.value = 'Generating...'
    }
    
    // Show initial progress
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove('hidden')
    }
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '5%'
    }
    if (this.hasMessageTarget) {
      this.messageTarget.textContent = 'Starting implementation plan generation...'
    }
    
    // Submit form via fetch to ensure async
    fetch(event.target.action, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.handleError(data.error)
      } else {
        console.log("[ImplementationPlan] Job queued:", data)
      }
    })
    .catch(error => {
      console.error("[ImplementationPlan] Error:", error)
      this.handleError("Failed to start generation: " + error.message)
    })
  }
}